import { DialogResult } from '../enums';
import { KeyValuePair } from '@brokerportal/common/enums';

// https://stackoverflow.com/questions/27936772/how-to-deep-merge-instead-of-shallow-merge
export function mergeArray<T>(target: Array<T>, source: Array<T>, uniqueKeyName: string) {
    const mergedDict: { [key: string]: T } = {};

    [...target, ...source].forEach(_ => {
        mergedDict[_[uniqueKeyName]] = _;
    });

    return Object.keys(mergedDict).map(key => mergedDict[key]);
}

export function isEmpty(str: string | null | undefined): boolean {
    return !str || 0 === str.length;
}

export const isNullOrUndefined = (object: any): object is undefined => object === null || object === undefined;

export function parseFilenameFromContentDisposition(contentDisposition: string | null) {
    if (!contentDisposition) {
        return null;
    }

    let filename = '';
    if (contentDisposition && contentDisposition.indexOf('attachment') !== -1) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(contentDisposition);
        if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, '');
        }
    }
    return filename;
}

export function isDialogResult(value: any): value is DialogResult {
    return Object.values(DialogResult).includes(value);
}

export function isEmptyInputValue(value: any): boolean {
    /**
     * Check if the object is a string or array before evaluating the length attribute.
     * This avoids falsely rejecting objects that contain a custom length attribute.
     * For example, the object {id: 1, length: 0, width: 0} should not be returned as empty.
     */
    return value == null || ((typeof value === 'string' || Array.isArray(value)) && value.length === 0);
}

export const toQueryString = (queryObj: {}) => {
    return Object.keys(queryObj)
        .filter(k => !isNullOrUndefined(queryObj[k]))
        .map(k => `${k}=${encodeURIComponent(queryObj[k])}`)
        .join('&');
};

export const pascalToSentence = value => {
    const sentence = value.replace(/([A-Z])/g, ' $1').trim();
    return sentence.charAt(0).toUpperCase() + sentence.slice(1).toLowerCase();
};

export const fromDictToKeyValuePairs = (obj: { [key: string]: any }): KeyValuePair[] => {
    return Object.entries(obj).map(([key, value]) => ({ key, value }));
};

export const EnumHelper = {
    parse: <T>(enumType: T, enumKey: any): T[keyof T] | undefined => {
        const foundKey = Object.keys(enumType).find(key => key === enumKey) as keyof T;
        const foundEnum = enumType[foundKey];
        return foundEnum;
    },
    cast: <T>(enumType: T, val: any): T[keyof T] | undefined => {
        return Object.values(enumType).includes(val) ? val : undefined;
    }
};

/**
 * Utility function to download blob content
 * @param {Blob} blob - The blob object
 * @param {string} filename - The name to save the file as
 */
export function downloadBlob(blob: Blob, filename: string) {
    const link = document.createElement('a');
    const url = window.URL.createObjectURL(blob);
    link.href = url;
    link.download = filename;
    try {
        link.click();
    } finally {
        // Defer revocation to the next tick to avoid intermittent download failures
        setTimeout(() => window.URL.revokeObjectURL(url), 0);
    }
}

export const WABSLoanLimit = 10000;

/**
 * Converts a local-midnight Date (e.g. from Angular Material date picker) to a UTC-midnight Date,
 * so that JSON serialisation produces the correct calendar date regardless of timezone.
 */
export function toUtcDate(date: Date): Date {
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
}
